<template>
  <div class="survey-management">
    <div class="flex justify-between items-center mb-6">
      <h2 class="text-2xl font-bold text-primary">Survey Management</h2>
      <button
        @click="openSurveyBuilder"
        class="bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark transition duration-300"
      >
        Add New Survey
      </button>
    </div>

    <div v-if="loading" class="text-center py-12">
      <div class="spinner"></div>
    </div>

    <div class="mb-8">
      <h3 class="text-xl font-semibold text-gray-700 mb-4">
        University-level Module Questionnaire
      </h3>
      <SurveyTable
        :surveys="universitySurveys"
        @edit="editSurvey"
        @delete="deleteSurveyAction"
        @publish="pubSurvey"
        @revoke="revokeSurvey"
        @preview="openPreviewModal"
        @detail="openDetailModal"
      />
    </div>

    <div class="mb-8">
      <h3 class="text-xl font-semibold text-gray-700 mb-4">
        School/Academy-level Module Questionnaire
      </h3>
      <SurveyTable
        :surveys="schoolDepartmentSurveys"
        @edit="editSurvey"
        @delete="deleteSurveyAction"
        @publish="pubSurvey"
        @revoke="revokeSurvey"
        @preview="openPreviewModal"
        @detail="openDetailModal"
      />
    </div>

    <SurveyBuilder
      v-if="showSurveyBuilder"
      :is-editing="isEditing"
      :initial-survey="currentSurvey"
      :available-departments="availableDepartments"
      :available-modules="availableModules"
      :is-super-admin="userRole === 'admin'"
      @close="closeSurveyBuilder"
      @submit="submitSurvey"
    />
    <SurveyPreviewModal
      v-if="showPreviewModal"
      :show="showPreviewModal"
      :survey="previewSurvey"
      :availableModules="availableModules"
      @close="closePreviewModal"
    />
    <SurveyDetailModal
      v-if="showDetailModal"
      :show="showDetailModal"
      :survey="currentDetailSurvey"
      @close="closeDetailModal"
      @save="saveModuleDates"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import SurveyBuilder from "./SurveyBuilder.vue";
import SurveyPreviewModal from "./modals/SurveyPreviewModal.vue";
import SurveyDetailModal from "./modals/SurveyDetailModal.vue";
import SurveyTable from "./SurveyTable.vue";

export default {
  name: "SurveyManagement",
  components: {
    SurveyBuilder,
    SurveyPreviewModal,
    SurveyTable,
    SurveyDetailModal,
  },
  data() {
    return {
      showSurveyBuilder: false,
      isEditing: false,
      currentSurvey: {
        id: "",
        title: "",
        description: "",
        type: "",
        targetModules: [],
        option: "",
        department: "",
        status: "Draft",
        startDate: null,
        endDate: null,
        questions: [],
      },
      showPreviewModal: false,
      previewSurvey: null,
      showDetailModal: false,
      currentDetailSurvey: null,
      loading: false,
    };
  },
  computed: {
    ...mapState({
      user: (state) => state.auth.user,
      surveys: (state) => state.surveys.surveys,
      userRole: (state) => state.auth.userRole,
      userDepartment: (state) => state.auth.userDepartment,
      userSchool: (state) => state.auth.userSchool,
      schools: (state) => state.schools,
      departments: (state) => state.departments,
      modules: (state) => state.modules,
    }),
    universitySurveys() {
      if (this.userRole === "admin") {
        return this.surveys.filter((survey) => survey.type === "University");
      }
      else
        return this.surveys.filter((survey) => survey.type === "University" && survey.status == 'Published');
    },
    schoolDepartmentSurveys() {
      return this.surveys.filter(
        (survey) => survey.type === "School/Department"
      );
    },
    availableDepartments() {
      if (this.userRole === "admin") {
        return [
          ...this.schools.map((s) => s.fullName),
          ...this.departments.map((d) => d.fullName),
        ];
      } else if (this.userRole === "school_admin") {
        const userSchool = this.schools.find(
          (s) => s.fullName === this.userSchool
        );
        const userDepartments = this.departments.filter(
          (d) => d.school === userSchool.shortName
        );
        return [userSchool.fullName, ...userDepartments.map((d) => d.fullName)];
      } else if (this.userRole === "department_admin") {
        const userDept = this.departments.find(
          (d) => d.fullName === this.userDepartment
        );
        return userDept ? [userDept.fullName] : [];
      }
      return [];
    },
    availableModules() {
      if (this.userRole === "admin") {
        return this.modules;
      } else if (this.userRole === "school_admin") {
        return this.modules.filter((m) => m.school === this.userSchool);
      } else if (this.userRole === "department_admin") {
        return this.modules.filter((m) => m.department === this.userDepartment);
      }
      return [];
    },
  },
  methods: {
    ...mapActions("surveys", [
      "fetchSurveys",
      "addSurvey",
      "updateSurvey",
      "deleteSurvey",
      "publishSurvey",
      "revokeSurvey",
    ]),
    openSurveyBuilder() {
      this.isEditing = false;
      this.currentSurvey = {
        id: "",
        title: "",
        description: "",
        type: this.userRole === "admin" ? "" : "School/Department",
        targetModules: [],
        option: "",
        department: "",
        status: "Draft",
        startDate: null,
        endDate: null,
        questions: [],
      };
      this.showSurveyBuilder = true;
    },
    editSurvey(survey) {
      this.isEditing = true;
      this.currentSurvey = { ...survey };
      this.showSurveyBuilder = true;
    },
    closeSurveyBuilder() {
      this.showSurveyBuilder = false;
      this.currentSurvey = {
        id: "",
        title: "",
        description: "",
        type: "",
        targetModules: [],
        option: "",
        department: "",
        status: "Draft",
        startDate: null,
        endDate: null,
        questions: [],
      };
    },
    async submitSurvey(survey) {
      try {
        if (this.isEditing) {
          await this.updateSurvey(survey);
        } else {
          await this.addSurvey(survey);
        }
        this.closeSurveyBuilder();
        this.fetchSurveys();
      } catch (error) {
        console.error("Error submitting survey:", error);
        alert("An error occurred while saving the survey. Please try again.");
      }
    },
    async deleteSurveyAction(id) {
      if (confirm("Are you sure you want to delete this survey?")) {
        try {
          await this.deleteSurvey(id);
          this.fetchSurveys();
        } catch (error) {
          console.error("Error deleting survey:", error);
          alert(
            "An error occurred while deleting the survey. Please try again."
          );
        }
      }
    },
    async pubSurvey(id) {
      try {
        const survey = this.surveys.find((s) => s.id === id);
        if (!survey) {
          throw new Error("Survey not found");
        }

        const dateValidationError = this.validateSurveyDates(survey);
        if (dateValidationError) {
          alert(dateValidationError);
          return;
        }

        await this.publishSurvey(id);
        this.fetchSurveys();
      } catch (error) {
        console.error("Error publishing survey:", error);
        alert(
          "An error occurred while publishing the survey. Please try again."
        );
      }
    },
    async revokeSurvey(id) {
      try {
        await this.revokeSurvey(id);
        this.fetchSurveys();
      } catch (error) {
        console.error("Error revoking survey:", error);
        alert("An error occurred while revoking the survey. Please try again.");
      }
    },
    validateSurveyDates(survey) {
      const now = new Date();
      const startDate = new Date(survey.startDate);
      const endDate = new Date(survey.endDate);

      if (startDate <= now) {
        return "Start date must be in the future";
      }

      if (endDate <= now) {
        return "End date must be in the future";
      }

      if (endDate <= startDate) {
        return "End date must be after the start date";
      }

      return null;
    },
    openPreviewModal(survey) {
      this.previewSurvey = { ...survey };
      this.showPreviewModal = true;
    },
    closePreviewModal() {
      this.showPreviewModal = false;
      this.previewSurvey = null;
    },
    openDetailModal(survey) {
      this.currentDetailSurvey = { ...survey };
      this.showDetailModal = true;
    },
    closeDetailModal() {
      this.showDetailModal = false;
      this.currentDetailSurvey = null;
    },
    async saveModuleDates({ surveyId, moduleDates }) {
      try {
        this.loading = true;
        //await this.updateSurveyModuleDates({ surveyId, moduleDates });
        this.closeDetailModal();
        this.fetchSurveys();
        this.loading = false;
      } catch (error) {
        console.error("Error saving module dates:", error);
        alert("An error occurred while saving module dates. Please try again.");
      }
    },
  },
  created() {
    this.loading = true;
    this.fetchSurveys();
    this.loading = false;
  },
};
</script>

<style scoped>
.survey-management {
  @apply p-6;
}
</style>